import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  orderBy,
  query,
  Timestamp,
  writeBatch,
  updateDoc,
  addDoc,
  serverTimestamp,
  deleteField,
  FieldValue,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import type { CommunityQuestion, CommunityQuestionOption, ParsedQuestion } from '../types/community-questions';

const COLLECTION_NAME = 'community_questions';

export interface CreateQuestionData {
  text: string;
  description?: string;
  learning?: string;
  type: 'question' | 'poll';
  options: CommunityQuestion['options'];
  optionsOrder: string[];
}

export interface UpdateQuestionData {
  text: string;
  description?: string;
  learning?: string;
  type: 'question' | 'poll';
  options: CommunityQuestion['options'];
  optionsOrder: string[];
}

class CommunityQuestionsService {
  private static instance: CommunityQuestionsService;

  private constructor() {}

  public static getInstance(): CommunityQuestionsService {
    if (!CommunityQuestionsService.instance) {
      CommunityQuestionsService.instance = new CommunityQuestionsService();
    }
    return CommunityQuestionsService.instance;
  }

  public async getAllQuestions(): Promise<CommunityQuestion[]> {
    const questionsRef = collection(db, COLLECTION_NAME);
    const q = query(questionsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        text: data.text,
        description: data.description,
        learning: data.learning,
        type: data.type,
        options: data.options,
        optionsOrder: data.optionsOrder,
        totalResponses: data.totalResponses,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(data.updatedAt),
      } as CommunityQuestion;
    });
  }

  public async deleteQuestion(questionId: string): Promise<void> {
    const questionRef = doc(db, COLLECTION_NAME, questionId);
    await deleteDoc(questionRef);
  }

  public async createQuestion(data: CreateQuestionData): Promise<CommunityQuestion> {
    const questionsCollection = collection(db, COLLECTION_NAME);

    const now = new Date();
    const docRef = await addDoc(questionsCollection, {
      text: data.text,
      description: data.description || null,
      learning: data.learning || null,
      type: data.type,
      options: data.options,
      optionsOrder: data.optionsOrder,
      totalResponses: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Return the created question with client-side date for immediate UI update
    return {
      id: docRef.id,
      text: data.text,
      description: data.description,
      learning: data.learning,
      type: data.type,
      options: data.options,
      optionsOrder: data.optionsOrder,
      totalResponses: 0,
      createdAt: now,
      updatedAt: now,
    };
  }

  public async updateQuestion(questionId: string, data: UpdateQuestionData): Promise<CommunityQuestion> {
    const questionRef = doc(db, COLLECTION_NAME, questionId);

    // Fetch current question to determine which options to delete
    const currentQuestionSnap = await getDoc(questionRef);
    if (!currentQuestionSnap.exists()) {
      throw new Error('Question not found');
    }

    const currentData = currentQuestionSnap.data();
    const currentOptions = currentData.options || {};
    const newOptionIds = new Set(Object.keys(data.options));

    // Build update object with field-level updates for options to preserve responseCount
    const updateData: Record<string, string | string[] | boolean | number | FieldValue | null> = {
      text: data.text,
      description: data.description || null,
      learning: data.learning || null,
      type: data.type,
      optionsOrder: data.optionsOrder,
      updatedAt: serverTimestamp(),
    };

    // Build merged options preserving responseCount
    const mergedOptions: Record<string, CommunityQuestionOption> = {};
    Object.entries(data.options).forEach(([optionId, option]) => {
      updateData[`options.${optionId}.label`] = option.label;
      updateData[`options.${optionId}.isCorrect`] = option.isCorrect ?? false;

      // Preserve existing responseCount or initialize to 0 for new options
      const responseCount = currentOptions[optionId]?.responseCount ?? 0;
      updateData[`options.${optionId}.responseCount`] = responseCount;
      
      mergedOptions[optionId] = {
        label: option.label,
        isCorrect: option.isCorrect ?? false,
        responseCount,
      };
    });

    // Delete options that were removed
    Object.keys(currentOptions).forEach((optionId) => {
      if (!newOptionIds.has(optionId)) {
        updateData[`options.${optionId}`] = deleteField();
      }
    });

    await updateDoc(questionRef, updateData);

    // Return the updated question with client-side date for immediate UI update
    const now = new Date();
    return {
      id: questionId,
      text: data.text,
      description: data.description,
      learning: data.learning,
      type: data.type,
      options: mergedOptions,
      optionsOrder: data.optionsOrder,
      totalResponses: currentData.totalResponses || 0,
      createdAt: currentData.createdAt?.toDate() || now,
      updatedAt: now,
    };
  }

  public async uploadQuestions(questions: ParsedQuestion[]): Promise<number> {
    const batch = writeBatch(db);
    const questionsCollection = collection(db, COLLECTION_NAME);

    questions.forEach((question) => {
      const docRef = doc(questionsCollection);
      batch.set(docRef, question);
    });

    await batch.commit();
    return questions.length;
  }
}

export const communityQuestionsService = CommunityQuestionsService.getInstance();
